import React from 'react';

export const ArchitectureDiagram: React.FC = () => {
  const diagram = `
┌─────────────────────────────────────────────────────────────────────────────────────────┐
│                              CloudFlare Fullstack Architecture                          │
└─────────────────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────────────────┐
│                                    🌐 CLIENT LAYER                                      │
├─────────────────────────────────────────────────────────────────────────────────────────┤
│                                                                                         │
│  ┌─────────────────┐     ┌─────────────────┐     ┌─────────────────┐                 │
│  │   📱 Web App    │     │  📚 Docs Site   │     │ 🎮 Quest Games  │                 │
│  │                 │     │                 │     │                 │                 │
│  │ • React + Vite  │     │ • Documentation │     │ • SES Quest     │                 │
│  │ • Tailwind CSS  │     │ • Quick Start   │     │ • Stripe Quest  │                 │
│  │ • TypeScript    │     │ • Claude Intel  │     │ • Gamification  │                 │
│  │ • Auth Context  │     │ • API Guides    │     │ • XP System     │                 │
│  └────────┬────────┘     └────────┬────────┘     └────────┬────────┘                 │
│           │                       │                       │                           │
│           └───────────────────────┴───────────────────────┘                           │
│                                   │                                                   │
│                                   ▼                                                   │
│                          ┌─────────────────┐                                         │
│                          │   🔀 Router     │                                         │
│                          │   Port: 3000    │                                         │
│                          └────────┬────────┘                                         │
└───────────────────────────────────┼─────────────────────────────────────────────────┘
                                   │
                                   │ HTTP/HTTPS
                                   ▼
┌─────────────────────────────────────────────────────────────────────────────────────────┐
│                                    ☁️  EDGE LAYER                                       │
├─────────────────────────────────────────────────────────────────────────────────────────┤
│                                                                                         │
│  ┌─────────────────────┐     ┌─────────────────────┐     ┌─────────────────────┐    │
│  │    🚀 API Worker    │     │  📁 Files Worker   │     │  📧 Email Worker   │    │
│  │    Port: 8787       │     │    Port: 8788      │     │    Port: 8789      │    │
│  │                     │     │                     │     │                     │    │
│  │ • Hono Framework    │     │ • R2 Operations    │     │ • SES Integration  │    │
│  │ • Auth/JWT          │     │ • File Upload      │     │ • Email Templates  │    │
│  │ • CORS Enabled      │     │ • File Listing     │     │ • Queue Handler    │    │
│  │ • Health Checks     │     │ • Service Binding  │     │ • Notifications    │    │
│  └──────────┬──────────┘     └──────────┬──────────┘     └──────────┬──────────┘    │
│             │                            │                            │               │
│             └────────────────────────────┴────────────────────────────┘               │
│                                         │                                             │
│                                         ▼                                             │
└─────────────────────────────────────────┼─────────────────────────────────────────────┘
                                         │
                                         │
┌─────────────────────────────────────────┼─────────────────────────────────────────────┐
│                                    💾 DATA LAYER                                       │
├─────────────────────────────────────────────────────────────────────────────────────────┤
│                                         │                                             │
│  ┌─────────────────┐     ┌─────────────────┐     ┌─────────────────┐              │
│  │   🗄️ D1 Database │     │   🪣 R2 Storage  │     │   🧠 Workers AI  │              │
│  │                 │     │                 │     │                 │              │
│  │ • Users Table   │     │ • File Objects  │     │ • Text Gen      │              │
│  │ • Sessions      │     │ • Images        │     │ • Embeddings    │              │
│  │ • App Data      │     │ • Documents     │     │ • Analysis      │              │
│  │ • SQLite Edge   │     │ • S3 Compatible │     │ • Built-in      │              │
│  └─────────────────┘     └─────────────────┘     └─────────────────┘              │
│                                                                                      │
│  ┌─────────────────┐     ┌─────────────────────────────────────────┐              │
│  │   🔑 KV Cache   │     │         📦 Shared Packages              │              │
│  │                 │     │                                         │              │
│  │ • Session Store │     │  • @cf/types  - TypeScript types      │              │
│  │ • Rate Limits   │     │  • @cf/utils  - Shared utilities      │              │
│  │ • Feature Flags │     │  • @cf/config - Configuration         │              │
│  └─────────────────┘     └─────────────────────────────────────────┘              │
└──────────────────────────────────────────────────────────────────────────────────────┘

┌─────────────────────────────────────────────────────────────────────────────────────────┐
│                                 🛠️  DEVELOPMENT TOOLS                                   │
├─────────────────────────────────────────────────────────────────────────────────────────┤
│                                                                                         │
│  • Turbo Monorepo  - Parallel builds and caching                                      │
│  • TypeScript      - End-to-end type safety                                           │
│  • Wrangler        - CloudFlare CLI and local dev                                     │
│  • Vitest          - Fast unit testing                                                │
│  • ESLint          - Code quality                                                     │
│  • Prettier        - Code formatting                                                  │
│  • Husky           - Git hooks                                                        │
│                                                                                         │
└─────────────────────────────────────────────────────────────────────────────────────────┘

                              🔄 Request Flow Example:
                              
    User → Web App → API Worker → D1 Database
      ↑                  ↓
      └──────────────────┘
         JSON Response
  `;

  return (
    <div className="min-h-screen bg-gray-900 text-green-400 p-8">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold mb-8 text-center text-white">
          System Architecture
        </h1>
        
        <div className="bg-black rounded-lg p-6 overflow-x-auto">
          <pre className="font-mono text-xs sm:text-sm whitespace-pre">
            {diagram}
          </pre>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4 text-blue-400">Key Benefits</h2>
            <ul className="space-y-2 text-gray-300">
              <li>• Edge computing with global distribution</li>
              <li>• Serverless architecture - no servers to manage</li>
              <li>• Integrated AI capabilities</li>
              <li>• Cost-effective scaling</li>
              <li>• Type-safe from frontend to backend</li>
            </ul>
          </div>

          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-xl font-bold mb-4 text-blue-400">Port Assignments</h2>
            <ul className="space-y-2 text-gray-300">
              <li>• Web App: <code className="bg-gray-700 px-2 py-1 rounded">localhost:3000</code></li>
              <li>• Docs Site: <code className="bg-gray-700 px-2 py-1 rounded">localhost:3002</code></li>
              <li>• API Worker: <code className="bg-gray-700 px-2 py-1 rounded">localhost:8787</code></li>
              <li>• Files Worker: <code className="bg-gray-700 px-2 py-1 rounded">localhost:8788</code></li>
              <li>• Email Worker: <code className="bg-gray-700 px-2 py-1 rounded">localhost:8789</code></li>
            </ul>
          </div>
        </div>

        <div className="mt-8 bg-gray-800 rounded-lg p-6">
          <h2 className="text-xl font-bold mb-4 text-blue-400">Quick Commands</h2>
          <div className="space-y-3">
            <div>
              <code className="bg-gray-700 px-3 py-2 rounded block">
                npm run dev
              </code>
              <p className="text-gray-400 text-sm mt-1">Start all services in development mode</p>
            </div>
            <div>
              <code className="bg-gray-700 px-3 py-2 rounded block">
                npm run build
              </code>
              <p className="text-gray-400 text-sm mt-1">Build all packages for production</p>
            </div>
            <div>
              <code className="bg-gray-700 px-3 py-2 rounded block">
                npm run deploy
              </code>
              <p className="text-gray-400 text-sm mt-1">Deploy to CloudFlare edge network</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};